'use client';
import Grid2 from '@mui/material/Grid2';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { BUTTONTYPE } from '@/types/button-type';
import { VARIANT } from '@/types/variant';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import CheckMardCircle from '../../../../public/icons/checkmard-circle';
import { useTranslations } from 'next-intl';

const Image = dynamic(() => import('@/components/common/image'), { ssr: false });
const Button = dynamic(() => import('@/components/common/button'), { ssr: false });

interface Service {
    title: string;
    description: string;
    price: string;
    iconUrl: string;
    iconColor: string;
    features: string[];
    paymentLink: string;
    related?: string;
    className?: string;
}

interface ServiceCardProps {
    service: Service;
    isMobile: boolean;
}

const getServices = (t: ReturnType<typeof useTranslations<'packages'>>): Service[] => [
    {
        title: t('services.visa_service.title'),
        related: 'Visa',
        className: 'Visa',
        description: t('services.visa_service.description'),
        price: t('services.visa_service.price'),
        iconUrl: '/icons/purple-plane.svg',
        iconColor: '#6687FF',
        features: [
            t('services.visa_service.features.group_chat'),
            t('services.visa_service.features.sop_writing'),
            t('services.visa_service.features.document_submission'),
            t('services.visa_service.features.legal_case'),
            t('services.visa_service.features.spousal_work_permit'),
            t('services.visa_service.features.children_visa'),
            t('services.visa_service.features.instalment_payment'),
        ],
        paymentLink: '/payments/new-payment/information?service=visa-service-first',
    },
    // {
    //     title: t('services.add_ons.title'),
    //     related: 'Funding',
    //     className: 'Add-Ons',
    //     description: t('services.add_ons.description'),
    //     price: t('services.add_ons.price'),
    //     iconUrl: '/icons/addon-funding.svg',
    //     iconColor: '#6788ff',
    //     features: [
    //         t('services.add_ons.features.everything_basic'),
    //         t('services.add_ons.features.extra_emails'),
    //         t('services.add_ons.features.edit_templates'),
    //         t('services.add_ons.features.generate_emails'),
    //         t('services.add_ons.features.follow_ups'),
    //     ],
    //     paymentLink: '/payments/new-payment/information?service=funding-service',
    // },
];

const ServiceCard: React.FC<ServiceCardProps> = ({ service, isMobile }) => {
    const router = useRouter();
    const t = useTranslations('packages');

    return (
        <Box
            sx={{
                borderRadius: '10px',
                backgroundColor: 'white',
                height: '100%',
            }}
            className={'__ServiceCard_1__'}
        >
            <Stack
                direction="column"
                p={{ xs: 3, lg: 5 }}
                spacing={3}
                className={'__ServiceCard_2__'}
            >
                <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    className={'__ServiceCard_3__'}
                >
                    <Image alt={`${service.title} icon`} url={service.iconUrl} className="w-13" />
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        fontWeight={700}
                        sx={{ color: service.iconColor }}
                        className={'__ServiceCard_4__'}
                    >
                        {service.title}
                    </Typography>
                </Stack>

                <Typography
                    variant={isMobile ? 'body1' : 'h6'}
                    sx={{ color: 'var(--color-darkText)', minHeight: { lg: '96px' } }}
                    className={'__ServiceCard_5__'}
                >
                    {service.description}
                </Typography>

                <Typography
                    variant={isMobile ? 'h5' : 'h4'}
                    fontWeight={700}
                    sx={{ color: 'var(--color-darkText)', py: { xs: 3, lg: 4 } }}
                    className={'__ServiceCard_6__'}
                >
                    {service.price}
                </Typography>

                <Image alt="divider" url="/divider.svg" className="w-full" />

                <Stack
                    direction="column"
                    spacing={2}
                    py={{ xs: 2, lg: 3 }}
                    className={'__ServiceCard_7__'}
                >
                    {service.features.map((feature, index) => (
                        <Stack key={index} direction="row" spacing={2} alignItems="center">
                            <CheckMardCircle />
                            <Typography sx={{ color: 'var(--color-darkText)' }}>{feature}</Typography>
                        </Stack>
                    ))}
                </Stack>

                <Button
                    disabled={false}
                    aria_label={`${service.title.toLowerCase()}-button`}
                    text={t('services.button.upgrade')}
                    handleClick={() => router.push(service.paymentLink)}
                    type={BUTTONTYPE.BUTTON}
                    variant={VARIANT.OUTLINED}
                    borderRadius={isMobile ? undefined : 12}
                    font={isMobile ? undefined : 15}
                    className="bg-white w-full"
                />
            </Stack>
        </Box>
    );
};

interface ServiceListContainerProps {
    activeTab: 'admissions' | 'funding' | 'visa';
}

export default function ServiceListContainer({ activeTab }: ServiceListContainerProps) {
    const isMobile = useMediaQuery('(max-width:1024px)');
    const t = useTranslations('packages');

    // Filter services based on active tab
    const filteredServices = getServices(t).filter(service => {
        if (activeTab === 'visa') {
            return service.title.toLowerCase().includes('visa');
        }
        if (activeTab === 'funding') {
            return service.title.toLowerCase().includes('funding') || service.related?.toLowerCase()?.includes('funding');
        }
        return false; // admissions tab shows no services
    });

    return (
        <Grid2 className={'__ServiceListContainer__'} container spacing={3}>
            {
                filteredServices.map((service, index) => (
                    <Grid2 className={`__${service.className}__`} key={index} size={{ xs: 12, md: 12, lg: 6 }}>
                        <ServiceCard service={service} isMobile={isMobile} />
                    </Grid2>
                ))
            }
        </Grid2>
    );
}
